import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProductListParams, ProductStatusFilter, ProductType } from '@/types/product';

export interface ProductQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  status: ProductStatusFilter;
  productTypes: ProductType[];
  brandIds: string[];
  categoryIds: string[];
  imprintValueIds: string[];
  dateFrom: string;
  dateTo: string;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  status: ProductStatusFilter | null;
  productTypes: ProductType[] | null;
  brandIds: string[] | null;
  categoryIds: string[] | null;
  imprintValueIds: string[] | null;
  dateFrom: string | null;
  dateTo: string | null;
}>;

const DEFAULTS: ProductQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'name',
  sortOrder: 'asc',
  search: '',
  status: 'all',
  productTypes: [],
  brandIds: [],
  categoryIds: [],
  imprintValueIds: [],
  dateFrom: '',
  dateTo: '',
};

function parseProductTypes(searchParams: URLSearchParams): ProductType[] {
  const legacy = searchParams.get('productType');
  const values = searchParams.getAll('productTypes');

  const raw =
    values.length > 1
      ? values
      : (values[0] ?? legacy ?? '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

  return raw.filter((item): item is ProductType => item === 'simple' || item === 'variable');
}

function parseIdList(searchParams: URLSearchParams, key: string): string[] {
  const values = searchParams.getAll(key);

  if (values.length > 1) {
    return values.filter(Boolean);
  }

  const single = values[0] ?? '';
  if (!single) return [];

  return single
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function useProductQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<ProductQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc',
      search: searchParams.get('search') || '',
      status: (searchParams.get('status') === 'active' || searchParams.get('status') === 'inactive'
        ? searchParams.get('status')
        : 'all') as ProductStatusFilter,
      productTypes: parseProductTypes(searchParams),
      brandIds: parseIdList(searchParams, 'brandIds'),
      categoryIds: parseIdList(searchParams, 'categoryIds'),
      imprintValueIds: parseIdList(searchParams, 'imprintValueIds'),
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
    };
  }, [searchParams]);

  const apiParams = useMemo<ProductListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.productTypes.length ? { productTypes: state.productTypes } : {}),
      ...(state.brandIds.length ? { brandIds: state.brandIds } : {}),
      ...(state.categoryIds.length ? { categoryIds: state.categoryIds } : {}),
      ...(state.imprintValueIds.length ? { imprintValueIds: state.imprintValueIds } : {}),
      ...(state.dateFrom ? { dateFrom: state.dateFrom } : {}),
      ...(state.dateTo ? { dateTo: state.dateTo } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (options?.resetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.dateFrom !== undefined) apply('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) apply('dateTo', patch.dateTo);

          if (patch.productTypes !== undefined) {
            next.delete('productType');
            next.delete('productTypes');
            if (patch.productTypes?.length) {
              next.set('productTypes', patch.productTypes.join(','));
            }
          }

          if (patch.brandIds !== undefined) {
            next.delete('brandIds');
            if (patch.brandIds?.length) {
              next.set('brandIds', patch.brandIds.join(','));
            }
          }

          if (patch.categoryIds !== undefined) {
            next.delete('categoryIds');
            if (patch.categoryIds?.length) {
              next.set('categoryIds', patch.categoryIds.join(','));
            }
          }

          if (patch.imprintValueIds !== undefined) {
            next.delete('imprintValueIds');
            if (patch.imprintValueIds?.length) {
              next.set('imprintValueIds', patch.imprintValueIds.join(','));
            }
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('search');
        next.delete('status');
        next.delete('productType');
        next.delete('productTypes');
        next.delete('brandIds');
        next.delete('categoryIds');
        next.delete('imprintValueIds');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.status !== 'all' ||
      state.productTypes.length > 0 ||
      state.brandIds.length > 0 ||
      state.categoryIds.length > 0 ||
      state.imprintValueIds.length > 0 ||
      state.dateFrom !== '' ||
      state.dateTo !== '' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
